import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Task } from "@/contexts/TaskContext";
import type { Database, Json } from "@/integrations/supabase/types";
import { mapDbTaskToTask } from "@/lib/taskMapper";
import { TASK_SELECT_COLUMNS } from "@/lib/taskSelectColumns";
import { paginatedTaskKeys } from "./usePaginatedTasksQuery";

type TaskInsert = Database["public"]["Tables"]["tasks"]["Insert"];
type TaskUpdate = Database["public"]["Tables"]["tasks"]["Update"];

const toJson = (value: unknown): Json => value as Json;

// Cache times in milliseconds
const STALE_TIME = 1 * 60 * 1000; // 1 minute - more responsive to changes
const CACHE_TIME = 30 * 60 * 1000; // 30 minutes

// Query keys
export const taskKeys = {
  all: ["tasks"] as const,
  lists: () => [...taskKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...taskKeys.lists(), filters] as const,
  details: () => [...taskKeys.all, "detail"] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  images: (id: string) => [...taskKeys.all, "images", id] as const,
};

// Fetch all tasks (with image URLs for thumbnails)
const fetchTasks = async (): Promise<Task[]> => {
  const { data, error } = await supabase
    .from("tasks")
    .select(TASK_SELECT_COLUMNS)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data || []).map(d => mapDbTaskToTask(d));
};

// Fetch task images
const fetchTaskImages = async (taskId: string): Promise<string[]> => {
  const { data, error } = await supabase
    .from("tasks")
    .select("image_urls, image_url")
    .eq("id", taskId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return [];
  }

  return data.image_urls || (data.image_url ? [data.image_url] : []);
};

// Hook to fetch all tasks with caching
export function useTasksQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: taskKeys.lists(),
    queryFn: fetchTasks,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    enabled: options?.enabled ?? true,
  });
}

// Hook to fetch task images with caching
export function useTaskImagesQuery(taskId: string | null) {
  return useQuery({
    queryKey: taskKeys.images(taskId || ""),
    queryFn: () => fetchTaskImages(taskId!),
    enabled: !!taskId,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });
}

// Add task mutation with optimistic update
export function useAddTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (task: Omit<Task, "id">) => {
      const taskInsert: TaskInsert = {
          question: task.question,
          instruction: task.instruction || null,
          sources: task.sources || null,
          answers: task.answers,
          correct_index: task.correctIndex,
          category: task.categories?.[0] || "",
          categories: task.categories || [],
          year: task.isPremium ? null : (task.year || null),
          document_type: task.isPremium ? null : (task.documentType || null),
          formula: task.isPremium ? null : (task.formula || null),
          task_number: task.taskNumber || null,
          subpoint: task.subpoint || null,
          image_url: task.imageUrls?.[0] || null,
          image_urls: task.imageUrls || [],
          image_thumbnails: task.imageThumbnails || null,
          image_compressed: task.imageCompressed || null,
          task_type: task.taskType || null,
          task_subtype: task.taskSubtype || null,
          use_yes_no: task.useYesNo || false,
          model_answer: task.modelAnswer || null,
          prefilled_answer: task.prefilledAnswer || null,
          operational_verb: task.operationalVerb || null,
          table_data: task.tableData ? toJson(task.tableData) : null,
          matching_data: task.matchingData ? toJson(task.matchingData) : null,
          strikethrough_text: task.strikethroughText || null,
          is_two_points: task.isTwoPoints || false,
          max_points: task.maxPoints || (task.isTwoPoints ? 2 : 1),
          is_multi_select: task.isMultiSelect || false,
          table_scoring_mode: task.tableScoringMode || null,
          custom_scoring_scenarios: task.customScoringScenarios ? toJson(task.customScoringScenarios) : null,
          verdict_options: task.verdictOptions ? toJson(task.verdictOptions) : null,
          multi_table_data: task.multiTableData ? toJson(task.multiTableData) : null,
          is_premium: task.isPremium || false,
          instruction_image_url: task.instructionImageUrl || null,
          instruction_image_thumbnail: task.instructionImageThumbnail || null,
          image_dimensions: task.imageDimensions ? toJson(task.imageDimensions) : null,
          instruction_image_width: task.instructionImageWidth || null,
          instruction_image_height: task.instructionImageHeight || null,
          image_blur_data: task.imageBlurData || null,
          instruction_image_blur: task.instructionImageBlur || null,
          // Bilingual PL TRANSLATION fields (main fields above hold EN original)
          question_pl: task.questionPl || null,
          instruction_pl: task.instructionPl || null,
          answers_pl: task.answersPl || null,
          table_data_pl: task.tableDataPl ? toJson(task.tableDataPl) : null,
          multi_table_data_pl: task.multiTableDataPl ? toJson(task.multiTableDataPl) : null,
          matching_data_pl: task.matchingDataPl ? toJson(task.matchingDataPl) : null,
          strikethrough_text_pl: task.strikethroughTextPl || null,
          prefilled_answer_pl: task.prefilledAnswerPl || null,
          sources_pl: task.sourcesPl || null,
          verdict_options_pl: task.verdictOptionsPl ? toJson(task.verdictOptionsPl) : null,
        };

      const { data, error } = await supabase
        .from("tasks")
        .insert(taskInsert)
        .select()
        .single();

      if (error) throw error;
      return mapDbTaskToTask(data);
    },
    onMutate: async (newTask) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: taskKeys.lists() });

      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData<Task[]>(taskKeys.lists());

      // Optimistically add the new task
      const optimisticTask: Task = {
        ...newTask,
        id: `temp-${Date.now()}`,
      };

      queryClient.setQueryData<Task[]>(taskKeys.lists(), (old) => 
        old ? [optimisticTask, ...old] : [optimisticTask]
      );

      return { previousTasks };
    },
    onError: (err, newTask, context) => {
      // Rollback on error
      if (context?.previousTasks) {
        queryClient.setQueryData(taskKeys.lists(), context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paginatedTaskKeys.all });
    },
  });
}

// Update task mutation with optimistic update
export function useUpdateTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Task> }) => {
      const dbUpdates: TaskUpdate = {};
      if (updates.question !== undefined) dbUpdates.question = updates.question;
      if (updates.instruction !== undefined) dbUpdates.instruction = updates.instruction;
      if (updates.sources !== undefined) dbUpdates.sources = updates.sources;
      if (updates.answers !== undefined) dbUpdates.answers = updates.answers;
      if (updates.correctIndex !== undefined) dbUpdates.correct_index = updates.correctIndex;
      if (updates.categories !== undefined) {
        dbUpdates.categories = updates.categories;
        dbUpdates.category = updates.categories[0] || "";
      }
      if (updates.year !== undefined) dbUpdates.year = updates.year;
      if (updates.documentType !== undefined) dbUpdates.document_type = updates.documentType;
      if (updates.formula !== undefined) dbUpdates.formula = updates.formula;
      if (updates.taskNumber !== undefined) dbUpdates.task_number = updates.taskNumber;
      if (updates.subpoint !== undefined) dbUpdates.subpoint = updates.subpoint;
      if (updates.taskType !== undefined) dbUpdates.task_type = updates.taskType;
      if (updates.taskSubtype !== undefined) dbUpdates.task_subtype = updates.taskSubtype;
      if (updates.useYesNo !== undefined) dbUpdates.use_yes_no = updates.useYesNo;
      if (updates.modelAnswer !== undefined) dbUpdates.model_answer = updates.modelAnswer;
      if (updates.prefilledAnswer !== undefined) dbUpdates.prefilled_answer = updates.prefilledAnswer;
      if (updates.operationalVerb !== undefined) dbUpdates.operational_verb = updates.operationalVerb;
      if (updates.tableData !== undefined) dbUpdates.table_data = updates.tableData ? toJson(updates.tableData) : null;
      if (updates.matchingData !== undefined) dbUpdates.matching_data = updates.matchingData ? toJson(updates.matchingData) : null;
      if (updates.strikethroughText !== undefined) dbUpdates.strikethrough_text = updates.strikethroughText;
      if (updates.isTwoPoints !== undefined) dbUpdates.is_two_points = updates.isTwoPoints;
      if (updates.maxPoints !== undefined) dbUpdates.max_points = updates.maxPoints;
      if (updates.isMultiSelect !== undefined) dbUpdates.is_multi_select = updates.isMultiSelect;
      if (updates.tableScoringMode !== undefined) dbUpdates.table_scoring_mode = updates.tableScoringMode;
      if (updates.customScoringScenarios !== undefined) dbUpdates.custom_scoring_scenarios = updates.customScoringScenarios ? toJson(updates.customScoringScenarios) : null;
      if (updates.verdictOptions !== undefined) dbUpdates.verdict_options = updates.verdictOptions ? toJson(updates.verdictOptions) : null;
      if (updates.multiTableData !== undefined) dbUpdates.multi_table_data = updates.multiTableData ? toJson(updates.multiTableData) : null;
      if (updates.isPremium !== undefined) dbUpdates.is_premium = updates.isPremium;
      if (updates.instructionImageUrl !== undefined) dbUpdates.instruction_image_url = updates.instructionImageUrl || null;
      if (updates.instructionImageThumbnail !== undefined) dbUpdates.instruction_image_thumbnail = updates.instructionImageThumbnail || null;
      if (updates.imageDimensions !== undefined) dbUpdates.image_dimensions = updates.imageDimensions ? toJson(updates.imageDimensions) : [];
      if (updates.instructionImageWidth !== undefined) dbUpdates.instruction_image_width = updates.instructionImageWidth || null;
      if (updates.instructionImageHeight !== undefined) dbUpdates.instruction_image_height = updates.instructionImageHeight || null;
      if (updates.imageBlurData !== undefined) dbUpdates.image_blur_data = updates.imageBlurData || [];
      if (updates.instructionImageBlur !== undefined) dbUpdates.instruction_image_blur = updates.instructionImageBlur || null;
      if (updates.imageUrls !== undefined) {
        dbUpdates.image_urls = updates.imageUrls;
        dbUpdates.image_url = updates.imageUrls[0] || null;
      }
      if (updates.imageThumbnails !== undefined) dbUpdates.image_thumbnails = updates.imageThumbnails;
      if (updates.imageCompressed !== undefined) dbUpdates.image_compressed = updates.imageCompressed;

      // Bilingual PL translation fields — `null` clears, `undefined` keeps existing
      if (updates.questionPl !== undefined) dbUpdates.question_pl = updates.questionPl || null;
      if (updates.instructionPl !== undefined) dbUpdates.instruction_pl = updates.instructionPl || null;
      if (updates.answersPl !== undefined) dbUpdates.answers_pl = updates.answersPl || null;
      if (updates.tableDataPl !== undefined) dbUpdates.table_data_pl = updates.tableDataPl ? toJson(updates.tableDataPl) : null;
      if (updates.multiTableDataPl !== undefined) dbUpdates.multi_table_data_pl = updates.multiTableDataPl ? toJson(updates.multiTableDataPl) : null;
      if (updates.matchingDataPl !== undefined) dbUpdates.matching_data_pl = updates.matchingDataPl ? toJson(updates.matchingDataPl) : null;
      if (updates.strikethroughTextPl !== undefined) dbUpdates.strikethrough_text_pl = updates.strikethroughTextPl || null;
      if (updates.prefilledAnswerPl !== undefined) dbUpdates.prefilled_answer_pl = updates.prefilledAnswerPl || null;
      if (updates.sourcesPl !== undefined) dbUpdates.sources_pl = updates.sourcesPl || null;
      if (updates.verdictOptionsPl !== undefined) dbUpdates.verdict_options_pl = updates.verdictOptionsPl ? toJson(updates.verdictOptionsPl) : null;

      const { error } = await supabase
        .from("tasks")
        .update(dbUpdates)
        .eq("id", id);

      if (error) throw error;
      return { id, updates };
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.lists() });

      const previousTasks = queryClient.getQueryData<Task[]>(taskKeys.lists());

      queryClient.setQueryData<Task[]>(taskKeys.lists(), (old) =>
        old?.map((task) =>
          task.id === id ? { ...task, ...updates } : task
        )
      );

      return { previousTasks };
    },
    onError: (err, variables, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(taskKeys.lists(), context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paginatedTaskKeys.all });
    },
  });
}

// Delete task mutation with optimistic update
export function useDeleteTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("tasks")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.lists() });

      const previousTasks = queryClient.getQueryData<Task[]>(taskKeys.lists());

      queryClient.setQueryData<Task[]>(taskKeys.lists(), (old) =>
        old?.filter((task) => task.id !== id)
      );

      return { previousTasks };
    },
    onError: (err, id, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(taskKeys.lists(), context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paginatedTaskKeys.all });
    },
  });
}
